/* ============================================================
   <gallery-slideshow>
   Custom element powering the gallery-slideshow section.
   - Desktop: thumb rail + fade between slides
   - Mobile : native horizontal scroll-snap (no JS needed for swipe;
              IntersectionObserver only mirrors the active state)
   - Theme editor: auto-navigate to a selected block
   ============================================================ */

class GallerySlideshow extends HTMLElement {
  constructor() {
    super();
    this._handlers = [];
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.gallery-slideshow__slide'));
    this.thumbs = Array.from(this.querySelectorAll('.gallery-slideshow__thumb'));
    this.dots = Array.from(this.querySelectorAll('.gallery-slideshow__dot'));
    this.thumbsTrack = this.querySelector('[data-thumbs-track]');
    this.track = this.querySelector('[data-track]');
    this.railArrowUp = this.querySelector('.gallery-slideshow__rail-arrow--up');
    this.railArrowDown = this.querySelector('.gallery-slideshow__rail-arrow--down');

    this.totalSlides = this.slides.length;
    this.activeIndex = 0;
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
    this.transition = parseInt(this.dataset.transition, 10) || 400;
    this.autoplayTimer = null;
    this._scrollSyncTimer = null;
    this._mobileSyncing = false;

    this.mqDesktop = window.matchMedia('(min-width: 750px)');
    this.mqReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (this.totalSlides <= 1) {
      this.classList.add('gallery-slideshow--single');
      return;
    }

    this._bindEvents();
    this._updateRailArrowsState();
    this._observeMobileScroll();

    if (this.autoplayEnabled) {
      this._startAutoplay();
    }
  }

  disconnectedCallback() {
    this._stopAutoplay();
    if (this._scrollObserver) this._scrollObserver.disconnect();
    if (this._railResizeObserver) this._railResizeObserver.disconnect();
    this._handlers.forEach(({ el, type, fn, opts }) => {
      el.removeEventListener(type, fn, opts);
    });
    this._handlers = [];
  }

  /* -------- Public navigation -------- */

  goTo(index, opts = {}) {
    if (index === this.activeIndex) return;
    if (index < 0 || index >= this.totalSlides) return;

    const previousIndex = this.activeIndex;
    this.activeIndex = index;

    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('is-active', isActive);
      if (isActive) {
        slide.setAttribute('aria-current', 'true');
      } else {
        slide.removeAttribute('aria-current');
      }
    });

    this.thumbs.forEach((thumb, i) => {
      const isActive = i === index;
      thumb.classList.toggle('is-active', isActive);
      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });

    /* Sync mobile scroll position when jump came from non-scroll source */
    if (!this.mqDesktop.matches && opts.from !== 'mobile-scroll') {
      this._scrollMobileToSlide(index);
    }

    /* Keep active thumb in view on the rail (desktop) */
    if (this.mqDesktop.matches) {
      this._scrollThumbIntoView(index);
    }

    this.dispatchEvent(new CustomEvent('gallery-slideshow:change', {
      bubbles: true,
      detail: { activeIndex: index, previousIndex }
    }));
  }

  next() {
    this.goTo((this.activeIndex + 1) % this.totalSlides, { from: 'api' });
  }

  prev() {
    this.goTo((this.activeIndex - 1 + this.totalSlides) % this.totalSlides, { from: 'api' });
  }

  /* -------- Internal -------- */

  _on(el, type, fn, opts) {
    if (!el) return;
    el.addEventListener(type, fn, opts);
    this._handlers.push({ el, type, fn, opts });
  }

  _bindEvents() {
    /* Thumb clicks */
    this.thumbs.forEach((thumb) => {
      this._on(thumb, 'click', () => {
        const idx = parseInt(thumb.dataset.thumbIndex, 10);
        this.goTo(idx, { from: 'thumb' });
        this._resetAutoplay();
      });
    });

    /* Dot clicks (mobile) */
    this.dots.forEach((dot) => {
      this._on(dot, 'click', () => {
        const idx = parseInt(dot.dataset.dotIndex, 10);
        this.goTo(idx, { from: 'dot' });
      });
    });

    /* Click on the main image -> next slide (desktop only) */
    this.slides.forEach((slide) => {
      const media = slide.querySelector('.gallery-slideshow__media');
      if (!media) return;
      this._on(media, 'click', (e) => {
        if (!this.mqDesktop.matches) return;
        if (e.target.closest('a, button')) return;
        this.next();
        this._resetAutoplay();
      });
    });

    /* Rail arrows */
    this._on(this.railArrowUp, 'click', () => this._scrollRail('up'));
    this._on(this.railArrowDown, 'click', () => this._scrollRail('down'));

    /* Rail scroll position -> arrow disabled state */
    this._on(this.thumbsTrack, 'scroll', () => {
      this._updateRailArrowsState();
    }, { passive: true });

    /* Track resize -> recompute (rail height changes when stage resizes) */
    if (this.thumbsTrack && 'ResizeObserver' in window) {
      this._railResizeObserver = new ResizeObserver(() => this._updateRailArrowsState());
      this._railResizeObserver.observe(this.thumbsTrack);
    }
    this._on(window, 'resize', () => this._updateRailArrowsState());

    /* Keyboard navigation when focus is inside the slideshow */
    this._on(this, 'keydown', (e) => {
      if (!this.contains(document.activeElement)) return;
      const tag = (document.activeElement.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
        this._resetAutoplay();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
        this._resetAutoplay();
      } else if (e.key === 'Home') {
        e.preventDefault();
        this.goTo(0, { from: 'keyboard' });
        this._resetAutoplay();
      } else if (e.key === 'End') {
        e.preventDefault();
        this.goTo(this.totalSlides - 1, { from: 'keyboard' });
        this._resetAutoplay();
      }
    });

    /* Pause autoplay on hover / focus */
    this._on(this, 'mouseenter', () => this._stopAutoplay());
    this._on(this, 'mouseleave', () => {
      if (this.autoplayEnabled) this._startAutoplay();
    });
    this._on(this, 'focusin', () => this._stopAutoplay());
    this._on(this, 'focusout', (e) => {
      if (!this.contains(e.relatedTarget) && this.autoplayEnabled) {
        this._startAutoplay();
      }
    });

    /* Theme editor: navigate to selected block */
    this._on(document, 'shopify:block:select', (e) => {
      if (!this.contains(e.target)) return;
      const slide = e.target.closest('.gallery-slideshow__slide');
      if (!slide) return;
      const idx = parseInt(slide.dataset.slideIndex, 10);
      this._stopAutoplay();
      this.goTo(idx, { from: 'editor' });
    });

    this._on(document, 'shopify:block:deselect', (e) => {
      if (!this.contains(e.target)) return;
      if (this.autoplayEnabled) this._startAutoplay();
    });

    /* Re-evaluate on viewport breakpoint change */
    const onMqChange = () => {
      this._updateRailArrowsState();
      if (!this.mqDesktop.matches) {
        this._scrollMobileToSlide(this.activeIndex);
      }
    };
    if (this.mqDesktop.addEventListener) {
      this.mqDesktop.addEventListener('change', onMqChange);
      this._handlers.push({ el: this.mqDesktop, type: 'change', fn: onMqChange });
    }
  }

  _scrollRail(direction) {
    if (!this.thumbsTrack || this.thumbs.length === 0) return;
    const thumbHeight = this.thumbs[0].offsetHeight || 88;
    const gap = 8;
    const stepCount = 3;
    const delta = (thumbHeight + gap) * stepCount;
    const target = this.thumbsTrack.scrollTop + (direction === 'up' ? -delta : delta);
    this.thumbsTrack.scrollTo({
      top: target,
      behavior: this.mqReducedMotion.matches ? 'auto' : 'smooth'
    });
  }

  _updateRailArrowsState() {
    if (!this.thumbsTrack || !this.railArrowUp || !this.railArrowDown) return;
    const { scrollTop, scrollHeight, clientHeight } = this.thumbsTrack;
    const noOverflow = scrollHeight <= clientHeight + 1;
    const atTop = scrollTop <= 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
    this.railArrowUp.disabled = noOverflow || atTop;
    this.railArrowDown.disabled = noOverflow || atBottom;
  }

  _scrollThumbIntoView(index) {
    const thumb = this.thumbs[index];
    if (!thumb || !this.thumbsTrack) return;

    const trackRect = this.thumbsTrack.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    const fullyVisible =
      thumbRect.top >= trackRect.top && thumbRect.bottom <= trackRect.bottom;

    if (fullyVisible) return;

    const offset =
      thumb.offsetTop - this.thumbsTrack.clientHeight / 2 + thumb.offsetHeight / 2;
    this.thumbsTrack.scrollTo({
      top: Math.max(0, offset),
      behavior: this.mqReducedMotion.matches ? 'auto' : 'smooth'
    });
  }

  _scrollMobileToSlide(index) {
    if (!this.track) return;
    const slide = this.slides[index];
    if (!slide) return;

    /* Guard against the IntersectionObserver echoing this back as
       "user scrolled" right after we trigger a programmatic scroll. */
    this._mobileSyncing = true;

    /* scrollIntoView with inline:'start' is direction-agnostic: it aligns the
       slide to the inline-start edge of the scroll container, which is the
       right edge in RTL and the left edge in LTR. This sidesteps the historic
       inconsistency of scrollLeft sign in RTL containers across browsers. */
    slide.scrollIntoView({
      behavior: this.mqReducedMotion.matches ? 'auto' : 'smooth',
      inline: 'start',
      block: 'nearest'
    });

    clearTimeout(this._scrollSyncTimer);
    this._scrollSyncTimer = setTimeout(() => {
      this._mobileSyncing = false;
    }, 600);
  }

  _observeMobileScroll() {
    if (!this.track || !('IntersectionObserver' in window)) return;

    const opts = {
      root: this.track,
      threshold: 0.6
    };

    this._scrollObserver = new IntersectionObserver((entries) => {
      if (this.mqDesktop.matches) return;
      if (this._mobileSyncing) return;

      let bestEntry = null;
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (!bestEntry || entry.intersectionRatio > bestEntry.intersectionRatio) {
            bestEntry = entry;
          }
        }
      });

      if (!bestEntry) return;
      const idx = parseInt(bestEntry.target.dataset.slideIndex, 10);
      if (Number.isNaN(idx) || idx === this.activeIndex) return;
      this.goTo(idx, { from: 'mobile-scroll' });
    }, opts);

    this.slides.forEach((slide) => this._scrollObserver.observe(slide));
  }

  _startAutoplay() {
    if (!this.autoplayEnabled) return;
    if (this.totalSlides <= 1) return;
    if (this.mqReducedMotion.matches) return;
    if (document.hidden) return;
    this._stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      if (document.hidden) return;
      this.next();
    }, this.autoplaySpeed);
  }

  _stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  _resetAutoplay() {
    if (!this.autoplayEnabled) return;
    this._stopAutoplay();
    this._startAutoplay();
  }
}

if (!customElements.get('gallery-slideshow')) {
  customElements.define('gallery-slideshow', GallerySlideshow);
}
